import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Trash2, ShieldCheck, User as UserIcon, Printer, Mail, Phone, Calendar, Package } from 'lucide-react';
import { adminApi, resolveUrl } from '@/services/adminApi';
import type { AdminUserDetail, AdminOrderListItem } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader, Button, LoadingState, Table, IconButton, StatusBadge, AlertDialog } from '@printtat/ui';
import type { Column } from '@printtat/ui';
import { formatPrice } from '@printtat/shared';
import { EditUserModal } from '@/components/EditUserModal';

const ROLE_COLORS: Record<string, 'purple' | 'blue' | 'gray'> = {
  admin: 'purple', printer: 'blue', user: 'gray',
};

const ROLE_ICONS: Record<string, typeof UserIcon> = {
  admin: ShieldCheck, printer: Printer, user: UserIcon,
};

const ORDER_STATUS_COLORS: Record<string, 'yellow' | 'blue' | 'indigo' | 'purple' | 'green' | 'red' | 'gray'> = {
  pending: 'yellow', confirmed: 'blue', processing: 'indigo', shipped: 'purple', delivered: 'green', cancelled: 'red',
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const displayName = (u: AdminUserDetail) => {
  if (u.firstName || u.lastName) return [u.firstName, u.lastName].filter(Boolean).join(' ');
  return u.email.split('@')[0];
};

export const AdminUserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data: user, isLoading } = useQuery<AdminUserDetail>({
    queryKey: [...queryKeys.users.all(), id],
    queryFn: () => adminApi.users.detail(id!),
    enabled: !!id,
  });

  const updateUser = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string; role?: string; isVerified?: boolean }) =>
      adminApi.users.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setEditing(false);
    },
  });

  const deleteUser = useMutation({
    mutationFn: () => adminApi.users.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setConfirmingDelete(false);
      navigate('/users');
    },
  });

  const isSelf = currentUser?.id === id;

  if (isLoading || !user) {
    return (
      <div className="px-8 py-6">
        <LoadingState variant="spinner" message="Loading user..." />
      </div>
    );
  }

  const orderColumns: Column<AdminOrderListItem>[] = [
    {
      key: 'orderNumber',
      header: 'Order',
      render: (o) => <span className="text-sm font-medium text-gray-900">{o.orderNumber}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (o) => <StatusBadge label={o.status} color={ORDER_STATUS_COLORS[o.status] ?? 'gray'} size="xs" />,
    },
    {
      key: 'items',
      header: 'Items',
      render: (o) => <span className="text-sm text-gray-600">{o.itemCount}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      render: (o) => <span className="text-sm font-medium text-gray-900">{formatPrice(o.totalAmount)}</span>,
    },
    {
      key: 'payment',
      header: 'Payment',
      render: (o) => (
        <span className={`text-xs font-medium ${o.paymentStatus === 'paid' ? 'text-green-600' : 'text-gray-500'}`}>
          {o.paymentStatus}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (o) => <span className="text-sm text-gray-500">{formatDate(o.createdAt)}</span>,
    },
  ];

  return (
    <div className="px-8 py-6 overflow-y-auto h-screen">
      <div className="flex items-start gap-3">
        <IconButton size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </IconButton>
        <div className="flex-1">
          <PageHeader
            icon={UserIcon}
            title={displayName(user)}
            description={user.email}
            actions={
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setEditing(true)}>
                  <Pencil size={14} className="mr-1" />
                  Edit
                </Button>
                {!isSelf && (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setConfirmingDelete(true)}>
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                )}
              </div>
            }
          />
        </div>
      </div>

      {/* User info */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-xl bg-white p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Profile</h3>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
              {user.profilePicture ? (
                <img src={resolveUrl(user.profilePicture!)} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <UserIcon size={20} className="text-gray-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{displayName(user)}</p>
              {(() => { const Icon = ROLE_ICONS[user.role] ?? UserIcon; return <StatusBadge label={user.role} color={ROLE_COLORS[user.role] ?? 'gray'} icon={<Icon size={12} />} size="xs" />; })()}
            </div>
          </div>
        </div>

        <div className="border rounded-xl bg-white p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Contact</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail size={14} className="text-gray-400" />
              {user.email}
            </div>
            {user.phoneNumber && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={14} className="text-gray-400" />
                {user.phoneNumber}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar size={14} className="text-gray-400" />
              Joined {formatDate(user.createdAt)}
            </div>
          </div>
        </div>

        <div className="border rounded-xl bg-white p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Stats</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Orders</span>
              <span className="font-medium text-gray-900">{user.orderCount}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Verified</span>
              <span className={`font-medium ${user.isVerified ? 'text-green-600' : 'text-gray-400'}`}>
                {user.isVerified ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Orders */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <Package size={18} className="text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900">Orders</h2>
          <span className="text-sm text-gray-400">({user.orders.length})</span>
        </div>

        {user.orders.length === 0 ? (
          <div className="border rounded-xl bg-white p-8 text-center text-sm text-gray-400">
            No orders yet
          </div>
        ) : (
          <Table
            columns={orderColumns}
            data={user.orders}
            keyExtractor={(o) => o.id}
          />
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <EditUserModal
          user={user}
          open={editing}
          onOpenChange={(open) => { if (!open) setEditing(false); }}
          onSave={(data) => updateUser.mutate(data)}
          loading={updateUser.isPending}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={confirmingDelete}
        onOpenChange={(open) => { if (!open) setConfirmingDelete(false); }}
        title="Delete user"
        description={`Permanently delete ${displayName(user)} (${user.email})? Their orders, designs, addresses, and assets will be removed.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteUser.isPending}
        onConfirm={() => deleteUser.mutate()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
};
